import {
  useQuery,
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { AdminApiResult } from './api';
import { isNotFound, unwrap } from './unwrap';

/** What a caller may tune on top of the key and the fetch: stale time, polling, `enabled`. */
export type ApiQueryOptions<T, K extends QueryKey> = Omit<
  UseQueryOptions<T, Error, T, K>,
  'queryKey' | 'queryFn'
>;

export type ApiQueryResult<T> = UseQueryResult<T, Error> & {
  /** The read answered 404: the record does not exist, which a detail screen shows as such. */
  readonly notFound: boolean;
};

/**
 * `useQuery` over an API call, with the result object turned into an exception by
 * `unwrap` — the step every screen used to write out by hand, and the one RV-B4 found
 * skipped: a `queryFn` that returns `null` on failure is cached as a successful empty
 * answer and never retried.
 *
 * `fn` returns the client's `{ ok, data } | { ok, error }`; the query's `data` is the
 * data, and its `error` the `ApiError`. `notFound` is that error read as a 404.
 */
export function useApiQuery<T, K extends QueryKey>(
  key: K,
  fn: () => Promise<AdminApiResult<T>>,
  options: ApiQueryOptions<T, K> = {},
): ApiQueryResult<T> {
  const query = useQuery({ ...options, queryKey: key, queryFn: async () => unwrap(await fn()) });
  // A getter rather than a spread: the result tracks which fields a component reads,
  // and copying it would read — and re-render on — every one of them.
  return Object.defineProperty(query, 'notFound', {
    get: () => isNotFound(query.error),
    enumerable: true,
  }) as ApiQueryResult<T>;
}
